import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogContentText, Link } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { SanitizedMarkdown } from '../../../components/sanitized-markdown';
import { Timestamp } from '../../../components/timestamp';
import { ButtonWithProgress } from '../../../components/button-with-progress';
import { createAbsoluteURL } from '../../../utils';
import { MainContext } from '../../../context';
import { cardSurface } from '../../../components/page-primitives';
import { IconTile } from '../settings/settings-primitives';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation } from 'react-router';
import { usePublisherAgreementText, useSignPublisherAgreement } from './use-publisher-agreement';
export const UserPublisherAgreement = props => {
    const { service, pageSettings, handleError } = useContext(MainContext);
    const location = useLocation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const agreementUrl = pageSettings.urls.publisherAgreement;
    const { data: agreementText } = usePublisherAgreementText(agreementUrl ?? '', {
        enabled: dialogOpen && !!agreementUrl
    });
    const { mutate: signAgreement, isPending: signing } = useSignPublisherAgreement();
    const closePublisherAgreement = () => {
        setDialogOpen(false);
    };
    const signPublisherAgreement = () => {
        signAgreement(undefined, { onSuccess: () => setDialogOpen(false) });
    };
    const openPublisherAgreement = () => {
        if (!agreementUrl) {
            handleError({ error: 'Publisher agreement text is not available.' }, { title: 'Couldn’t load the publisher agreement' });
        }
        else {
            setDialogOpen(true);
        }
    };
    const onClose = () => {
        setDialogOpen(false);
    };
    const user = props.user;
    if (!user.publisherAgreement) {
        return null;
    }
    const publisherAgreementName = pageSettings?.publisherAgreement?.name ?? '';
    const publisherAgreementSigned = user.publisherAgreement.status == 'signed';
    const loginProvider = user.publisherAgreement.loginProvider;
    const loginProviderName = user.publisherAgreement.loginProviderName || loginProvider;
    const outdated = user.publisherAgreement.status === 'outdated';
    const required = outdated ? (_jsxs(_Fragment, { children: ["The ", publisherAgreementName, " Publisher Agreement changed since you signed it. You need to sign the new version before you can publish any extension to this registry."] })) : (_jsxs(_Fragment, { children: ["You need to sign the ", publisherAgreementName, " Publisher Agreement before you can publish any extension to this registry."] }));
    let title;
    let description;
    let action;
    if (publisherAgreementSigned) {
        title = 'Publisher Agreement signed';
        description = user.publisherAgreement.timestamp ? (_jsxs(_Fragment, { children: ["You signed the ", publisherAgreementName, " Publisher Agreement", ' ', _jsx(Timestamp, { value: user.publisherAgreement.timestamp }), "."] })) : (_jsxs(_Fragment, { children: ["You signed the ", publisherAgreementName, " Publisher Agreement."] }));
        action = (_jsx(Button, { onClick: openPublisherAgreement, variant: 'outlined', children: "View" }));
    }
    else if (user.publisherAgreement.status === 'revoked') {
        title = 'Publisher Agreement revoked';
        description = (_jsxs(_Fragment, { children: ["Your ", publisherAgreementName, " Publisher Agreement was revoked, so you cannot publish. Contact an administrator to sign it again."] }));
        action = null;
    }
    else if (!loginProvider || user.additionalLogins?.find(login => login.provider === loginProvider)) {
        title = outdated ? 'Publisher Agreement updated' : 'Publisher Agreement required';
        description = required;
        action = (_jsx(Button, { onClick: openPublisherAgreement, variant: 'outlined', color: 'secondary', children: "Review & sign" }));
    }
    else {
        title = outdated ? 'Publisher Agreement updated' : 'Publisher Agreement required';
        description = (_jsxs(_Fragment, { children: [required, " To start the signing process, please log in with ", loginProviderName, "."] }));
        action = (_jsx(Link, { href: createAbsoluteURL([service.serverUrl, 'oauth2', 'authorization', loginProvider], 
            // the server returns to this page once the login is linked
            [{ key: 'redirect', value: location.pathname + location.search + location.hash }]), children: _jsxs(Button, { variant: 'outlined', color: 'secondary', children: ["Log in with ", loginProviderName] }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: theme => ({
                    ...cardSurface(theme),
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.875rem',
                    p: '1rem 1.125rem',
                    mt: '1.125rem'
                }), children: [_jsx(IconTile, { sx: { width: '2.125rem', height: '2.125rem' }, children: publisherAgreementSigned ? (_jsx(CheckIcon, { sx: { fontSize: '1.0625rem' } })) : (_jsx(DescriptionOutlinedIcon, { sx: { fontSize: '1.0625rem' } })) }), _jsxs(Box, { sx: { flex: 1, minWidth: '12.5rem' }, children: [_jsx(Typography, { sx: { fontSize: '0.875rem', fontWeight: 600 }, children: title }), _jsx(Typography, { sx: { fontSize: '0.78125rem', color: 'text.disabled', mt: '0.125rem' }, children: description })] }), action] }), _jsx(Dialog, { open: dialogOpen, onClose: onClose, maxWidth: 'xl', sx: { '& .MuiDialog-paperScrollPaper': { height: '75%', width: '100%' } }, children: _jsx(DialogContent, { children: agreementText ? (_jsxs(DialogContentText, { component: 'div', children: [_jsx(SanitizedMarkdown, { content: agreementText, sanitize: false, linkify: false }), _jsxs(Box, { display: 'flex', justifyContent: 'flex-end', children: [_jsx(Button, { onClick: closePublisherAgreement, children: "Close" }), !publisherAgreementSigned && (_jsx(ButtonWithProgress, { working: signing, onClick: signPublisherAgreement, children: "Agree" }))] })] })) : (_jsx(Box, { height: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(CircularProgress, { color: 'secondary' }) })) }) })] }));
};
//# sourceMappingURL=user-publisher-agreement.js.map